import { useState, useCallback, useRef } from "react";
import { getData } from "../api/apiService";
import { processLocation } from "../utils/location-utils";

/**
 * Popup opens as soon as files/metadata are ready.
 * Spatial extent loads in the background and fills the map when done.
 */
const usePopup = (customHeaders = {}) => {
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [popupDetails, setPopupDetails] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);
  const [spatialExtent, setSpatialExtent] = useState(null);
  // Invalidate in-flight spatial fetches when closing or opening another model.
  const requestIdRef = useRef(0);

  const handleOpenPopup = useCallback(
    async (details) => {
      const requestId = ++requestIdRef.current;
      try {
        setPopupLoading(true);
        setSpatialExtent(null);

        const directory = processLocation(details.location_name);

        // 1) Files first — metadata + downloads can show immediately
        const filesResponse = await getData(
          `/files/info/${directory}`,
          customHeaders,
        );
        if (requestId !== requestIdRef.current) return;

        const nextDetails = { ...details, files: filesResponse };
        setPopupDetails(nextDetails);
        setPopupOpen(true);
        setPopupLoading(false);

        // 2) Spatial in background — map updates when ready (does not block popup)
        try {
          if (!nextDetails.tdis_identifier) {
            if (requestId === requestIdRef.current) {
              setSpatialExtent([]);
            }
            return;
          }
          const spatialResponse = await getData(
            `/spatial/info/${nextDetails.tdis_identifier}`,
            customHeaders,
          );
          if (requestId !== requestIdRef.current) return;
          setSpatialExtent(
            Array.isArray(spatialResponse) ? spatialResponse : [],
          );
        } catch (spatialError) {
          console.error("Error loading spatial extent:", spatialError);
          if (requestId === requestIdRef.current) {
            setSpatialExtent([]);
          }
        }
      } catch (error) {
        console.error("Error opening pop-up:", error);
        if (requestId === requestIdRef.current) {
          setPopupLoading(false);
        }
      }
    },
    [customHeaders],
  );

  const handleClosePopup = useCallback(() => {
    requestIdRef.current += 1;
    setPopupOpen(false);
    setPopupDetails(null);
    setSpatialExtent(null);
    setPopupLoading(false);
  }, []);

  return {
    isPopupOpen,
    popupDetails,
    handleOpenPopup,
    handleClosePopup,
    popupLoading,
    spatialExtent,
  };
};

export default usePopup;
